import { useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "../components/EmptyState";
import PageBackBar from "../components/PageBackBar";
import { useAuth } from "../features/auth/useAuth";
import { isActivationChecked } from "../features/directory/activation";
import ActivationSummaryCard from "../features/directory/ActivationSummaryCard";
import LoggedInInfo from "../features/directory/LoggedInInfo";
import { extractAxiosDetail, fullName } from "../features/directory/format";
import { useActivationOverview, useWorkvivoDirectoryRefresh } from "../features/directory/hooks";
import type { ActivationOverviewLocation } from "../features/directory/types";
import WorkvivoSyncBar from "../features/directory/WorkvivoSyncBar";
import "../App.css";

type SortOrder = "lowest" | "name";

const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: "lowest", label: "Lowest % first" },
  { value: "name", label: "A–Z" }
];

function ownerName(loc: ActivationOverviewLocation) {
  return loc.owner ? fullName(loc.owner.first_name, loc.owner.last_name, loc.owner.email) : null;
}

function matchesSearch(loc: ActivationOverviewLocation, q: string) {
  if (!q) return true;
  const haystack = [
    loc.business_name,
    loc.store_id,
    loc.catalog_location,
    loc.catalog_concept,
    loc.owner?.email,
    ownerName(loc)
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q.toLowerCase());
}

function compareByName(a: ActivationOverviewLocation, b: ActivationOverviewLocation) {
  return a.business_name.localeCompare(b.business_name) || a.store_id.localeCompare(b.store_id);
}

/** Lowest login rate first; locations nobody has been checked at (rate null) go last. */
function compareByRate(a: ActivationOverviewLocation, b: ActivationOverviewLocation) {
  if (a.activation_rate === null && b.activation_rate === null) return compareByName(a, b);
  if (a.activation_rate === null) return 1;
  if (b.activation_rate === null) return -1;
  return a.activation_rate - b.activation_rate || compareByName(a, b);
}

function RateCell({ rate }: { rate: number | null }) {
  if (rate === null) {
    return <span className="directory-muted">—</span>;
  }
  const pct = Math.round(rate * 100);
  return (
    <div className="directory-activation-rate-cell">
      <span className="directory-activation-rate-value">{pct}%</span>
      <span className="directory-activation-bar" aria-hidden>
        <span className="directory-activation-bar-fill" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </span>
    </div>
  );
}

export default function ActivationOverview() {
  const { user } = useAuth();
  const isSuperuser = user?.role === "superuser";
  const searchId = useId();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortOrder>("lowest");
  const workvivoRefresh = useWorkvivoDirectoryRefresh();
  const query = useActivationOverview({ enabled: isSuperuser });

  const data = query.data;
  const q = search.trim();
  const rows = useMemo(() => {
    const locations = data?.locations ?? [];
    return locations.filter((loc) => matchesSearch(loc, q)).sort(sort === "lowest" ? compareByRate : compareByName);
  }, [data, q, sort]);

  if (!user || !isSuperuser) {
    return <div style={{ color: "#b91c1c" }}>You don’t have access to view Workvivo logins.</div>;
  }

  const checked = isActivationChecked(data?.activation_sync);
  const totalLocations = data?.locations.length ?? 0;

  return (
    <div className="directory-page">
      <PageBackBar />
      <header>
        <div className="directory-activation-title-row">
          <h1 className="approvals-page-title">Workvivo logins</h1>
          <LoggedInInfo />
        </div>
        <p className="approvals-page-sub">How many active people at each location have logged in to Workvivo.</p>
      </header>

      {data ? <WorkvivoSyncBar sync={data.sync} refresh={workvivoRefresh} /> : null}

      {query.isLoading ? (
        <div className="directory-state" role="status">
          Loading Workvivo logins…
        </div>
      ) : query.isError ? (
        <div className="directory-error" role="alert">
          <strong>Failed to load Workvivo logins.</strong> {extractAxiosDetail(query.error)}
        </div>
      ) : !data ? null : !checked ? (
        <EmptyState
          title="Workvivo logins haven’t been checked yet"
          description="Logins are read from Workvivo after each directory refresh. Numbers will appear here once the first check finishes."
        />
      ) : (
        <>
          <ActivationSummaryCard
            counts={data.totals}
            finishedAt={data.activation_sync.finished_at}
            title="All active locations"
          />

          <div className="directory-controls" role="search">
            <label htmlFor={searchId} className="visually-hidden">
              Search locations
            </label>
            <input
              id={searchId}
              type="search"
              className="directory-search"
              placeholder="Search by location, store ID or admin"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
            <div className="directory-segmented" role="group" aria-label="Sort locations">
              <span className="directory-segmented-label">Sort</span>
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={sort === opt.value}
                  data-active={sort === opt.value || undefined}
                  onClick={() => setSort(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {query.isFetching ? (
              <span className="directory-updating" role="status">
                Updating…
              </span>
            ) : null}
          </div>

          {totalLocations === 0 ? (
            <EmptyState
              title="No active locations yet"
              description="Active locations appear here once they are set up in the portal."
            />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No locations match your search"
              description={`Nothing matches “${q}”. Try a location name, store ID or admin.`}
            />
          ) : (
            <>
              <p className="directory-muted-small" role="status">
                Showing {rows.length} of {totalLocations} location{totalLocations === 1 ? "" : "s"}
              </p>
              <div className="directory-table-wrap">
                <table className="directory-table directory-table--activation">
                  <thead>
                    <tr>
                      <th scope="col">Location</th>
                      <th scope="col">Concept</th>
                      <th scope="col">Location admin</th>
                      <th scope="col" className="directory-col-num">
                        Active
                      </th>
                      <th scope="col" className="directory-col-num">
                        Logged in
                      </th>
                      <th scope="col" className="directory-col-num">
                        Not logged in yet
                      </th>
                      <th scope="col">% logged in</th>
                      <th scope="col">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((loc) => {
                      const admin = ownerName(loc);
                      return (
                        <tr key={loc.store_id}>
                          <td data-label="Location" className="directory-cell-primary">
                            <div className="directory-person">
                              <span className="directory-person-name">{loc.business_name}</span>
                              <span className="directory-person-email">{loc.store_id}</span>
                            </div>
                          </td>
                          <td data-label="Concept">
                            <span className={loc.catalog_concept ? "directory-cell-text" : "directory-muted"}>
                              {loc.catalog_concept || "—"}
                            </span>
                          </td>
                          <td data-label="Location admin">
                            {loc.owner ? (
                              <div className="directory-stack">
                                <span className="directory-cell-text">{admin}</span>
                                <span className="directory-muted-small">{loc.owner.email}</span>
                              </div>
                            ) : (
                              <span className="directory-muted-italic">No location admin</span>
                            )}
                          </td>
                          <td data-label="Active" className="directory-col-num">
                            <span className="directory-count">{loc.active}</span>
                          </td>
                          <td data-label="Logged in" className="directory-col-num">
                            <span className="directory-count">{loc.activated}</span>
                          </td>
                          <td data-label="Not logged in yet" className="directory-col-num">
                            <span className="directory-count">{loc.not_activated}</span>
                          </td>
                          <td data-label="% logged in">
                            <RateCell rate={loc.activation_rate} />
                          </td>
                          <td className="directory-cell-action">
                            {loc.owner ? (
                              <Link
                                to={`/location-users?owner=${encodeURIComponent(loc.owner.id)}&store=${encodeURIComponent(
                                  loc.store_id
                                )}`}
                                className="directory-action-link"
                                aria-label={`View users at ${loc.business_name}`}
                              >
                                View users <span aria-hidden>→</span>
                              </Link>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
